// ChartViewer.jsx — smooth pinch-to-zoom viewer for images + PDFs on iOS Safari.
//
// The receipt is initially "fit to window" — scaled so the entire width AND
// height fit inside the modal viewport with no scrolling. Users then pinch
// (touch) or Ctrl+wheel (desktop) to zoom in for detail. There is no button
// chrome — the document is the only thing on screen.

import { useEffect, useLayoutEffect, useMemo, useRef, useState, useCallback } from "react";
import { useGesture } from "@use-gesture/react";

// KEY DESIGN NOTES
// ─────────────────
// * `touch-action: none` on the gesture container so iOS Safari hands us every
//   touch event including the second finger.
// * Pan uses `translate3d()` + `will-change: transform` so pinch + pan run on
//   the GPU on iPad Safari.
// * Base "fit to viewport" is applied inside ChartItem (baseW/baseH). The
//   parent world transform then applies pinch/drag scale + pan on top, so a
//   pinch of 1.5× makes the already-fitted image 1.5× bigger — exactly what
//   the user expects.
// * Scale updates go through a low-pass filter so raw pinch jitter smooths out.

const ZOOM_MIN = 0.5;
const ZOOM_MAX = 6;
const SMOOTHING = 0.35;

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

export default function ChartViewer({ src, mime, accentColor = "#F59E0B", className = "" }) {
  const isPdf = mime === "application/pdf";
  const viewportRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [imgDims, setImgDims] = useState(null);

  const [viewportSize, setViewportSize] = useState({ w: 0, h: 0 });
  useLayoutEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const update = () => setViewportSize({ w: el.clientWidth, h: el.clientHeight });
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    setScale(1);
    setPan({ x: 0, y: 0 });
    setImgDims(null);
  }, [src]);

  const scaleRef = useRef(scale);
  const panRef = useRef(pan);
  useEffect(() => {
    scaleRef.current = scale;
  }, [scale]);
  useEffect(() => {
    panRef.current = pan;
  }, [pan]);

  const targetScaleRef = useRef(1);
  const targetPanRef = useRef({ x: 0, y: 0 });
  const rafId = useRef(0);
  const gesturingRef = useRef(false);
  const worldElRef = useRef(null);

  const applyTransform = useCallback(() => {
    const el = worldElRef.current;
    if (!el) return;
    el.style.transform = `translate3d(${panRef.current.x}px, ${panRef.current.y}px, 0) scale(${scaleRef.current})`;
  }, []);

  const tickSmooth = useCallback(() => {
    const s = scaleRef.current + (targetScaleRef.current - scaleRef.current) * SMOOTHING;
    const px = panRef.current.x + (targetPanRef.current.x - panRef.current.x) * SMOOTHING;
    const py = panRef.current.y + (targetPanRef.current.y - panRef.current.y) * SMOOTHING;
    scaleRef.current = s;
    panRef.current = { x: px, y: py };
    applyTransform();
    const settled =
      Math.abs(s - targetScaleRef.current) < 0.001 &&
      Math.abs(px - targetPanRef.current.x) < 0.5 &&
      Math.abs(py - targetPanRef.current.y) < 0.5;
    if (!settled || gesturingRef.current) {
      rafId.current = requestAnimationFrame(tickSmooth);
    } else {
      rafId.current = 0;
      scaleRef.current = targetScaleRef.current;
      panRef.current = { ...targetPanRef.current };
      applyTransform();
      setScale(scaleRef.current);
      setPan(panRef.current);
    }
  }, [applyTransform]);

  const kickSmoother = useCallback(() => {
    if (rafId.current) return;
    rafId.current = requestAnimationFrame(tickSmooth);
  }, [tickSmooth]);

  useEffect(
    () => () => {
      if (rafId.current) cancelAnimationFrame(rafId.current);
    },
    []
  );

  useGesture(
    {
      onPinchStart: () => {
        gesturingRef.current = true;
        kickSmoother();
      },
      onPinch: ({ offset: [s], origin: [ox, oy], memo }) => {
        const rect = viewportRef.current?.getBoundingClientRect();
        if (!rect) return memo;
        const anchorX = ox - rect.left;
        const anchorY = oy - rect.top;
        const anchor =
          memo || {
            startScale: scaleRef.current,
            startPan: { ...panRef.current },
            anchorX,
            anchorY,
          };
        const nextScale = clamp(s, ZOOM_MIN, ZOOM_MAX);
        const ratio = nextScale / anchor.startScale;
        targetScaleRef.current = nextScale;
        targetPanRef.current = {
          x: (anchor.startPan.x - anchor.anchorX) * ratio + anchor.anchorX,
          y: (anchor.startPan.y - anchor.anchorY) * ratio + anchor.anchorY,
        };
        kickSmoother();
        return anchor;
      },
      onPinchEnd: () => {
        gesturingRef.current = false;
        kickSmoother();
      },
      onDragStart: () => {
        gesturingRef.current = true;
        kickSmoother();
      },
      onDrag: ({ delta: [dx, dy], pinching }) => {
        if (pinching) return;
        targetPanRef.current = {
          x: targetPanRef.current.x + dx,
          y: targetPanRef.current.y + dy,
        };
        kickSmoother();
      },
      onDragEnd: () => {
        gesturingRef.current = false;
        kickSmoother();
      },
      onWheel: ({ event, ctrlKey, delta: [, dy] }) => {
        // @use-gesture wheel state does NOT expose `origin` (pinch-only).
        // Read the anchor from the raw DOM event's client coordinates.
        if (!ctrlKey && !event.metaKey) return;
        event.preventDefault();
        const rect = viewportRef.current?.getBoundingClientRect();
        if (!rect) return;
        const anchor = { x: event.clientX - rect.left, y: event.clientY - rect.top };
        const factor = dy > 0 ? 0.9 : 1.111;
        const nextScale = clamp(targetScaleRef.current * factor, ZOOM_MIN, ZOOM_MAX);
        targetPanRef.current = {
          x: (targetPanRef.current.x - anchor.x) * (nextScale / targetScaleRef.current) + anchor.x,
          y: (targetPanRef.current.y - anchor.y) * (nextScale / targetScaleRef.current) + anchor.y,
        };
        targetScaleRef.current = nextScale;
        kickSmoother();
      },
    },
    {
      target: viewportRef,
      eventOptions: { passive: false },
      pinch: {
        scaleBounds: { min: ZOOM_MIN, max: ZOOM_MAX },
        rubberband: true,
        from: () => [scaleRef.current, 0],
      },
      drag: { from: () => [panRef.current.x, panRef.current.y] },
    }
  );

  const onImgLoad = useCallback((e) => {
    const img = e.currentTarget;
    setImgDims({ w: img.naturalWidth, h: img.naturalHeight });
  }, []);

  const worldStyle = useMemo(
    () => ({
      transform: `translate3d(${pan.x}px, ${pan.y}px, 0) scale(${scale})`,
      transformOrigin: "0 0",
      willChange: "transform",
    }),
    [pan, scale]
  );

  return (
    <div
      className={`relative w-full h-full flex flex-col ${className}`}
      data-testid="chart-viewer"
    >
      <div
        ref={viewportRef}
        className="flex-1 overflow-hidden select-none"
        style={{ touchAction: "none", cursor: "grab" }}
      >
        <div ref={worldElRef} className="w-full h-full" style={worldStyle}>
          <div className="w-full h-full flex flex-col items-center justify-center gap-3">
            {isPdf ? (
              <PdfPages
                src={src}
                viewportWidth={viewportSize.w}
                viewportHeight={viewportSize.h}
                accentColor={accentColor}
              />
            ) : (
              <ChartItem
                src={src}
                alt="Receipt"
                width={imgDims?.w}
                height={imgDims?.h}
                viewportWidth={viewportSize.w}
                viewportHeight={viewportSize.h}
                onLoad={onImgLoad}
                testId="chart-image"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ChartItem — one page (image or PDF page). Fits into the viewport by BOTH
// width AND height so nothing scrolls at scale=1. Pinch scale is applied by
// the parent world transform on top of this base fit.
function ChartItem({ src, alt, width, height, viewportWidth, viewportHeight, onLoad, testId }) {
  const hasDims = width && height && viewportWidth && viewportHeight;
  // Fit-to-viewport: shrink so both dimensions fit; never upscale past 1×.
  const fit = hasDims
    ? Math.min(viewportWidth / width, viewportHeight / height, 1)
    : 1;
  const baseW = hasDims ? width * fit : null;
  const baseH = hasDims ? height * fit : null;

  return (
    <div
      className="shrink-0 flex items-center justify-center"
      style={
        baseW && baseH
          ? { width: `${baseW}px`, height: `${baseH}px` }
          : { maxWidth: "100%", maxHeight: "100%" }
      }
    >
      <img
        src={src}
        alt={alt}
        onLoad={onLoad}
        draggable={false}
        data-testid={testId}
        className="block pointer-events-none shadow-md"
        style={{
          width: baseW ? `${baseW}px` : "auto",
          height: baseH ? `${baseH}px` : "auto",
          maxWidth: baseW ? "none" : "100%",
          maxHeight: baseH ? "none" : "100%",
        }}
      />
    </div>
  );
}

function PdfPages({ src, viewportWidth, viewportHeight, accentColor }) {
  const [pages, setPages] = useState([]);
  const [status, setStatus] = useState("loading");
  const [errMsg, setErrMsg] = useState("");

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    setPages([]);
    setErrMsg("");
    (async () => {
      try {
        const pdfjs = await import("pdfjs-dist/build/pdf.mjs");
        if (!pdfjs.GlobalWorkerOptions.workerSrc) {
          pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";
        }
        const loadingTask = pdfjs.getDocument({ url: src });
        const pdf = await loadingTask.promise;
        const out = [];
        const RENDER_SCALE = 1.5;
        for (let p = 1; p <= pdf.numPages; p++) {
          if (cancelled) return;
          const page = await pdf.getPage(p);
          const viewport = page.getViewport({ scale: RENDER_SCALE });
          const canvas = document.createElement("canvas");
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          const ctx = canvas.getContext("2d");
          await page.render({ canvasContext: ctx, viewport }).promise;
          out.push({
            dataUrl: canvas.toDataURL("image/png"),
            width: viewport.width,
            height: viewport.height,
          });
        }
        if (!cancelled) {
          setPages(out);
          setStatus("ready");
        }
      } catch (err) {
        if (!cancelled) {
          setStatus("error");
          setErrMsg(err?.message || "Failed to render PDF");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [src]);

  if (status === "loading") {
    return (
      <div className="text-white/70 text-sm px-6 py-4" data-testid="chart-pdf-loading">
        Rendering PDF…
      </div>
    );
  }
  if (status === "error") {
    return (
      <div
        className="text-white/80 text-sm px-6 py-4 max-w-md text-center"
        data-testid="chart-pdf-error"
      >
        Couldn&rsquo;t render this PDF ({errMsg}).{" "}
        <a
          href={src}
          target="_blank"
          rel="noopener noreferrer"
          className="underline"
          style={{ color: accentColor }}
        >
          Open in a new tab
        </a>{" "}
        instead.
      </div>
    );
  }
  // Multi-page PDFs: give each page its own vertical row so the first page
  // is fully visible at scale=1 (fits both width and height of the viewport),
  // and subsequent pages sit below — reachable by pinching out + panning,
  // matching the "no initial scrolling" spec.
  return (
    <>
      {pages.map((p, i) => (
        <ChartItem
          key={i}
          src={p.dataUrl}
          alt={`Receipt page ${i + 1}`}
          width={p.width}
          height={p.height}
          viewportWidth={viewportWidth}
          viewportHeight={viewportHeight}
          testId={`chart-pdf-page-${i + 1}`}
        />
      ))}
    </>
  );
}
